import { useState, useEffect, useCallback } from 'react';
import {
  useParams,
  useNavigate,
  generatePath,
  useLocation,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SidePanel,
  SidePanelType,
  ButtonSecondary,
  ButtonPseudo,
  SidePanelLoader,
  PermissionEnum,
  Popup,
  ButtonPrimary,
  responseErrorsResolver,
  ResponseError,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  clearDocumentSidepanelState,
  deleteIsSuccess,
  onDocumentMove,
} from './store/slices';
import CorporateDocument from '../../../components/new-application/corporate/document-sidepanel';
import {
  useLazyGetCorporateApplicationDocumentFileQuery,
  useLazyGetCorporateNewApplicationDocumentQuery,
  useChangeCorporateApplicationDocumentStatusMutation,
  useChangeCorporateApplicationDocumentCategoryMutation,
  useBatchDeleteCorporateApplicationDocumentsMutation,
  useChangeNewApplicationDocumentExpirationDateMutation,
  useRenameCorporateDocumentMutation,
} from '../store/services';
import { useAddCustomersCommentMutation } from '../../../store/documents/services';
import {
  ApplicationDocument,
  ApplicationDocumentStatus,
  NewApplicationStatus,
} from '../types';
import { CustomerType } from '../../customers/types';

import ROUTES from '../../../routes';

import cn from '../new-application.module.css';

const CorporateDocumentSidepanel = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<
    ApplicationDocumentStatus | undefined
  >(undefined);
  const [imgLink, setImgLink] = useState('');
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);
  const [changeDateError, setChangeDateError] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [nameError, setNameError] = useState('');

  const { applicationId, documentId } = useParams();
  const { search } = useLocation();

  const {
    setError,
    formState: { errors },
  } = useForm();

  const {
    newApplications: {
      corporateInfo: { document: documentItem, newApplicationInfo, documents },
      list: { allowedDocumentsCategories },
    },
    customersDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const [getDocument, { isLoading: documentIsLoading }] =
    useLazyGetCorporateNewApplicationDocumentQuery();
  const [getDocumentFile, { isLoading, isFetching }] =
    useLazyGetCorporateApplicationDocumentFileQuery();
  const [
    changeCorporateDocumentStatus,
    { isLoading: statusChangeInProgress, isSuccess },
  ] = useChangeCorporateApplicationDocumentStatusMutation();
  const [changeCorporateDocumentCategory, { isLoading: moveInProgress }] =
    useChangeCorporateApplicationDocumentCategoryMutation();
  const [deleteCorporateAppicationDocuments, { isLoading: deleteInProgress }] =
    useBatchDeleteCorporateApplicationDocumentsMutation();
  const [addComment, { isLoading: commentInProgress }] =
    useAddCustomersCommentMutation();
  const [changeDocumentExpDate, { isLoading: updateInProgress }] =
    useChangeNewApplicationDocumentExpirationDateMutation();
  const [rename, { isLoading: renameInProgress }] =
    useRenameCorporateDocumentMutation();

  const dispatch = useAppDispatch();

  const onRename = async () => {
    if (documentName.length < 3) {
      setNameError('name must be longer than or equal to 3 characters');
    } else {
      setNameError('');

      const response = (await rename({
        id: applicationId as string,
        documentId: documentId as string,
        body: {
          name: documentName,
        },
      })) as { error?: ResponseError };

      if (response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
        });
      }
    }
  };

  const addCommentAction = (text: string) => {
    addComment({
      id: documentId as string,
      body: {
        text,
      },
    });
  };

  const getDocumentFileAction = async () => {
    const response = await getDocumentFile({
      id: applicationId as string,
      documentId: documentId as string,
    });

    if (response.data) {
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      setImgLink(blob);
    }
  };

  const onDelete = async () => {
    const response = await deleteCorporateAppicationDocuments({
      id: applicationId as string,
      documentId: [documentId as string],
    });

    if (response) {
      setDeletePopupVisible(false);

      const nextDocuments = documents?.filter((item) => item.id !== documentId);

      dispatch(deleteIsSuccess(nextDocuments as ApplicationDocument[]));
    }
  };

  const onChangeDocumentExpDate = async (date: string) => {
    const response = (await changeDocumentExpDate({
      id: documentId as string,
      body: {
        id: documentId as string,
        expirationDate: date,
      },
    })) as { error?: ResponseError | { data: { message: string } } };

    if (response.error) {
      if (Array.isArray(response?.error?.data?.message)) {
        if (response?.error?.data?.message[0]?.property === 'coreError') {
          setChangeDateError(
            response?.error?.data?.message[0]?.value as string,
          );
        }
      } else if (typeof response?.error?.data?.message === 'string') {
        setChangeDateError(response?.error?.data?.message);
      } else {
        setChangeDateError('Internal Server Error. Try Again');
      }
    }
  };

  const changeCorporateDocumentCategoryAction = async (
    documentCategoryId: string,
  ) => {
    const response = await changeCorporateDocumentCategory({
      id: applicationId as string,
      body: {
        ids: [documentId as string],
        documentCategoryId,
      },
    });

    if (response) {
      dispatch(onDocumentMove(documentCategoryId));
    }
  };

  const onDownload = () => {
    if (imgLink) {
      const hiddenElement = document.createElement('a');
      // @ts-ignore
      hiddenElement.href = imgLink;
      hiddenElement.target = '_blank';
      hiddenElement.download = `${documentItem?.name}`;
      hiddenElement.click();
    }
  };

  const navigate = useNavigate();

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);
    setDocumentName('');
    setNameError('');

    setTimeout(() => {
      dispatch(clearDocumentSidepanelState());

      const linkString = generatePath(
        ROUTES.CUSTOMERS_NEW_APPLICATION_CORPORATE_INFO,
        {
          applicationId: applicationId as string,
        },
      );

      navigate(`${linkString}${search || ''}`);
    }, 100);
  }, []);

  useEffect(() => {
    if (documentItem) {
      setDocumentName(documentItem.name);
    }
  }, [documentItem]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (documentId && applicationId) {
      getDocument({ id: applicationId, documentId });
      getDocumentFileAction();
    }
  }, [dispatch, documentId, applicationId]);

  useEffect(() => {
    if (isSuccess) {
      setSelectedStatus(undefined);
    }
  }, [isSuccess]);

  let content;

  if (documentIsLoading) {
    content = <SidePanelLoader />;
  } else {
    content = (
      <CorporateDocument
        document={documentItem}
        type={documentItem?.documentCategoryId || documentItem?.category}
        documentFileIsLoading={isLoading}
        onDownload={onDownload}
        downloadInProgress={isFetching}
        applicationStatus={newApplicationInfo?.status}
        imgLink={imgLink}
        moveInProgress={moveInProgress}
        setDeletePopupVisible={setDeletePopupVisible}
        addCommentAction={addCommentAction}
        onChangeCategory={changeCorporateDocumentCategoryAction}
        commentInProgress={commentInProgress}
        commentsVisible={activePermissions?.includes(
          PermissionEnum.DOCUMENTS_COMMENTS_ACTION,
        )}
        clientType={CustomerType.CORPORATE}
        updateAllowed={activePermissions?.includes(
          PermissionEnum.DOCUMENTS_UPDATE_ACTION,
        )}
        onChangeDocumentExpDate={onChangeDocumentExpDate}
        changeDateError={changeDateError}
        updateInProgress={updateInProgress}
        allowedCategories={allowedDocumentsCategories}
        documentName={documentName}
        setDocumentName={setDocumentName}
        nameError={nameError || (errors?.name?.message as string)}
        renameAvailable={activePermissions?.includes(
          PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_EDIT_ACTION,
        )}
      />
    );
  }

  return (
    <>
      <Popup
        title={`Delete ${documentItem?.name}`}
        onCancelClick={() => setDeletePopupVisible(false)}
        onOkClick={onDelete}
        cancelButtonText="Cancel"
        okButtonText={deleteInProgress ? 'Loading...' : 'Delete'}
        isVisible={deletePopupVisible}
        isLoading={deleteInProgress}
        parent={document.getElementById('delete-popup-wrapper') as HTMLElement}
      />
      <SidePanel
        type={SidePanelType.Customers}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={handleClose}
        title={documentItem?.name || ''}
        sidepanelExpandedKey="application-document"
        footer={
          <>
            {newApplicationInfo?.status !== NewApplicationStatus.ACCEPTED &&
              newApplicationInfo?.status !== NewApplicationStatus.DECLINED &&
              activePermissions?.includes(
                PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_UPDATE_STATUS_ACTION,
              ) &&
              documentItem && (
                <>
                  <ButtonPrimary
                    text={renameInProgress ? 'Loading...' : 'Save'}
                    onClick={onRename}
                    disabled={documentItem?.name === documentName}
                    bold
                  />
                  {documentItem?.status !==
                    ApplicationDocumentStatus.ACCEPTED &&
                    documentItem?.status !==
                      ApplicationDocumentStatus.EXPIRED && (
                      <ButtonSecondary
                        text={
                          statusChangeInProgress &&
                          selectedStatus === ApplicationDocumentStatus.ACCEPTED
                            ? 'Loading...'
                            : 'Accept'
                        }
                        onClick={() => {
                          setSelectedStatus(ApplicationDocumentStatus.ACCEPTED);

                          changeCorporateDocumentStatus({
                            id: applicationId as string,
                            body: {
                              ids: [documentItem?.id],
                              status: ApplicationDocumentStatus.ACCEPTED,
                            },
                          });
                        }}
                        disabled={statusChangeInProgress}
                        bold
                      />
                    )}
                  {documentItem?.status !==
                    ApplicationDocumentStatus.REJECTED &&
                    documentItem?.status !==
                      ApplicationDocumentStatus.EXPIRED && (
                      <ButtonSecondary
                        text={
                          statusChangeInProgress &&
                          selectedStatus === ApplicationDocumentStatus.REJECTED
                            ? 'Loading...'
                            : 'Reject'
                        }
                        className={cn.rejectStatusButton}
                        onClick={() => {
                          setSelectedStatus(ApplicationDocumentStatus.REJECTED);

                          changeCorporateDocumentStatus({
                            id: applicationId as string,
                            body: {
                              ids: [documentItem?.id],
                              status: ApplicationDocumentStatus.REJECTED,
                            },
                          });
                        }}
                        disabled={statusChangeInProgress}
                        bold
                      />
                    )}
                </>
              )}

            <ButtonPseudo
              text="Close"
              onClick={handleClose}
              className={`${cn.closeDocumentSidepanel} button-white`}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <div id="delete-popup-wrapper" />
    </>
  );
};

export default CorporateDocumentSidepanel;
